/**
 * 导购Agent - 常量
 */
import type { TextPlaceholder } from '@/types/dialogueConfig';
import type { GuidePromptTabItem, GuideTabItem } from '@/types/guideAgent';

/** 编辑页一级 tab：与线索Agent 保持一致，仅不含「评论回复」 */
export const GUIDE_TABS: GuideTabItem[] = [
  { key: 'switch-config', label: '基础配置', desc: '配置导购Agent是否回复与其他' },
  { key: 'greeting', label: '开口引导语', desc: '用户进线后未说话前，发送哪些消息' },
  { key: 'wake', label: '唤醒语', desc: '用户聊天中途未回复，发送哪些消息' },
  { key: 'product', label: '商品列表', desc: '维护从抖音侧同步下来的商品与人工补充信息' },
  { key: 'store', label: '店铺知识', desc: '按门店（渠道账号）维护通用问答知识' },
  { key: 'prompt', label: '提示词管理', desc: '配置导购Agent身份、铁律与回复决策' },
  { key: 'collect', label: '信息采集', desc: '导购Agent自动保存用户哪些信息' },
  { key: 'qa', label: '问答库', desc: '用户满足哪些信息，发送哪些消息' },
];

/** 提示词管理下的二级 tab */
export const GUIDE_PROMPT_TABS: GuidePromptTabItem[] = [
  {
    key: 'prompt-config',
    label: '提示词配置',
    hint: '按内容段维护导购Agent的身份、铁律、回复决策等提示词，可自行新增、改名与删除；正文里可插入 {动作标记_商品卡} 特殊标记，原样保存交给后端识别。',
  },
  {
    key: 'placeholder',
    label: '占位符配置',
    hint: '占位符是一段可复用的“物料引用”。命名后绑定一份物料（问答菜单，或抖音/小红书等渠道的留资卡、企微卡），即可在提示词配置中以 {占位符名} 的形式插入，发送时自动转换为对应物料。',
  },
];

/**
 * 提示词正文可插入的文本标记。
 *
 * 与线索Agent 的占位符不同：它不是后台可配置的物料引用，没有 id。
 * 界面上只展示 label，正文里存的是完整的 text，由后端识别这段特殊标记。
 */
export const GUIDE_PROMPT_TEXT_PLACEHOLDERS: TextPlaceholder[] = [
  { text: '{动作标记_商品卡}', label: '商品卡' },
];

/** 商品类型 */
export const GUIDE_PRODUCT_TYPE = {
  /** 团购套餐 */
  GROUP_BUY: 1,
  /** 代金券 */
  VOUCHER: 2,
  /** 上门服务 */
  DOOR_SERVICE: 3,
} as const;

/** 商品类型文案 */
export const GUIDE_PRODUCT_TYPE_LABEL: Record<number, string> = {
  [GUIDE_PRODUCT_TYPE.GROUP_BUY]: '团购套餐',
  [GUIDE_PRODUCT_TYPE.VOUCHER]: '代金券',
  [GUIDE_PRODUCT_TYPE.DOOR_SERVICE]: '上门服务',
};

/** 商品状态：标记该商品当前是否有效 */
export const GUIDE_PRODUCT_STATUS = {
  /** 无效 */
  INVALID: 0,
  /** 有效 */
  VALID: 1,
} as const;

/** 商品摘要向量化状态文案 */
export const GUIDE_SUMMARY_VECTOR_STATUS_LABEL: Record<number, string> = {
  0: '未向量化',
  1: '已向量化',
};

/** 商品同步任务状态（POST /products/sync 与 GET /products/sync-status 的 status） */
export const GUIDE_SYNC_TASK_STATUS = {
  /** 待处理（已入队，尚未开始） */
  PENDING: 0,
  /** 执行中 */
  RUNNING: 1,
  /** 成功 */
  SUCCESS: 2,
  /** 失败 */
  FAILED: 3,
} as const;

/** 同步任务状态轮询间隔：后端文档建议 3~5 秒 */
export const GUIDE_SYNC_POLL_INTERVAL_MS = 4000;

/** 知识（商品知识 / 店铺知识）向量化状态文案 */
export const GUIDE_KNOWLEDGE_VECTOR_STATUS_LABEL: Record<number, string> = {
  0: '待向量化',
  1: '向量成功',
  2: '向量失败',
};

/** 知识向量化状态对应的标签色 */
export const GUIDE_KNOWLEDGE_VECTOR_TAG_TYPE: Record<number, 'info' | 'success' | 'danger'> = {
  0: 'info',
  1: 'success',
  2: 'danger',
};

/** 已向量化：右侧头部「向量成功」计数按此状态统计 */
export const GUIDE_KNOWLEDGE_VECTOR_SUCCESS = 1;

/** 知识卡片每次加载条数：卡片流用「加载更多」翻页 */
export const GUIDE_KNOWLEDGE_PAGE_SIZE = 20;

/** 门店库省市区解析状态文案 */
export const GUIDE_STORE_PARSE_STATUS_LABEL: Record<number, string> = {
  0: '待解析',
  1: '已解析',
  2: '解析失败',
};

/** 门店库解析状态对应的标签色 */
export const GUIDE_STORE_PARSE_TAG_TYPE: Record<number, 'info' | 'success' | 'danger'> = {
  0: 'info',
  1: 'success',
  2: 'danger',
};

/** 门店库解析状态筛选项 */
export const GUIDE_STORE_PARSE_STATUS_OPTIONS = [
  { value: 1, label: '已解析' },
  { value: 0, label: '待解析' },
  { value: 2, label: '解析失败' },
];

/** 门店营业状态文案 */
export const GUIDE_STORE_STATUS_LABEL: Record<number, string> = {
  0: '停业',
  1: '营业',
};

/** 门店营业状态对应的标签色 */
export const GUIDE_STORE_STATUS_TAG_TYPE: Record<number, 'info' | 'success'> = {
  0: 'info',
  1: 'success',
};

/** 门店营业状态筛选项 */
export const GUIDE_STORE_STATUS_OPTIONS = [
  { value: 1, label: '营业' },
  { value: 0, label: '停业' },
];

/** 店铺知识下的二级 tab */
export const GUIDE_STORE_TABS = [
  { key: 'knowledge', label: '知识卡片' },
  { key: 'library', label: '门店库' },
] as const;

/** 地图初始视野：全国范围，中心取国土几何中心附近 */
export const GUIDE_STORE_MAP_CENTER: [number, number] = [108, 34];
export const GUIDE_STORE_MAP_ZOOM = 4;
/** 点击某家门店时放大到的层级 */
export const GUIDE_STORE_MAP_FOCUS_ZOOM = 15;
